import { useRef, useState, useEffect } from "react";
import { Upload, Loader2, Images, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LibraryModal({ onPick, onClose }) {
  const [files, setFiles] = useState(null);

  useEffect(() => {
    fetch(`${API}/admin/files`, { credentials: "include" })
      .then((r) => r.json())
      .then(setFiles)
      .catch(() => setFiles([]));
  }, []);

  return (
    <div data-testid="library-modal" className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-5" onClick={onClose}>
      <div className="bg-cream rounded-[2rem] p-6 md:p-8 max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display uppercase text-2xl text-ink">Upload Library</h3>
          <button onClick={onClose} data-testid="library-close-button" aria-label="Close"
            className="w-10 h-10 rounded-full bg-blush flex items-center justify-center text-ink hover:bg-petal transition-colors duration-300">
            <X size={16} />
          </button>
        </div>
        {!files ? (
          <p className="font-elegant italic text-xl text-gold animate-pulse">Opening the library</p>
        ) : files.length === 0 ? (
          <p className="text-sm text-neutral-500" data-testid="library-empty">Nothing uploaded yet. Use the Upload button to add your first image.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {files.map((f) => (
              <button
                key={f.id}
                data-testid={`library-item-${f.id}`}
                onClick={() => onPick(f.url)}
                className="group relative aspect-square overflow-hidden rounded-xl border-2 border-transparent hover:border-gold transition-colors duration-300"
                title={f.original_filename}
              >
                <img src={f.url} alt={f.original_filename} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ImageField({ label, value, onChange, testid, hint }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [libOpen, setLibOpen] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/admin/upload`, { method: "POST", credentials: "include", body: form });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Upload failed");
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gold mb-2">{label}</label>
      {hint && <p className="text-xs text-neutral-500 mb-2">{hint}</p>}
      <div className="flex gap-3 items-start flex-wrap">
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste an image URL, upload or pick from library"
          className="flex-1 min-w-[200px] border-2 border-ink/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold bg-white transition-colors duration-300"
        />
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} />
        <button
          type="button"
          data-testid={`${testid}-upload`}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="shrink-0 flex items-center gap-2 bg-ink text-cream rounded-full px-5 py-3 text-[10px] font-bold uppercase tracking-[0.18em] hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          Upload
        </button>
        <button
          type="button"
          data-testid={`${testid}-library`}
          onClick={() => setLibOpen(true)}
          className="shrink-0 flex items-center gap-2 border-2 border-ink/15 rounded-full px-5 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-ink/70 hover:border-gold hover:text-gold transition-colors duration-300"
        >
          <Images size={14} />
          Library
        </button>
      </div>
      {value && (
        <img src={value} alt="Preview" data-testid={`${testid}-preview`} className="mt-3 h-20 w-20 object-cover rounded-xl border-2 border-gold/20" />
      )}
      {libOpen && (
        <LibraryModal
          onClose={() => setLibOpen(false)}
          onPick={(url) => {
            onChange(url);
            setLibOpen(false);
            toast.success("Image added from library");
          }}
        />
      )}
    </div>
  );
}
